import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

const useHook = () => {

  // Redux store isn't typed yet — see components migrated so far, `any` is the interim convention.
  const productosFavoritos = useSelector((state: any) => state.favoritos.productos);
  const carrito = useSelector((state: any) => state.carrito.productos);

  const [menuIsOpen, setMenuIsOpen] = useState(false);

  // Sentinel-based "is it actually stuck" detection: a 0-height marker sits right
  // before the sticky header, and once it scrolls out of view the header must be
  // pinned to top:0, regardless of how tall the Topbar above it is.
  const [isSticky, setIsSticky] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsSticky(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  // Counts are persisted client-side (localStorage) and can differ from the
  // server's fresh state, so they're deferred a tick to avoid a hydration
  // mismatch on the badge numbers — everything else renders immediately.
  const [countsReady, setCountsReady] = useState(false);

  const [anchorFest, setAnchorFest] = useState<HTMLElement | null>(null);
  const [festividades, setFestividades] = useState<any[]>([]);
  const openFest = Boolean(anchorFest);

  const handleOpenFest = (e: React.MouseEvent<HTMLElement>) => setAnchorFest(e.currentTarget);
  const handleCloseFest = () => setAnchorFest(null);

  const fetchFestividades = async () => {
    const body = { festividad: true };
    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_URL_FRONTEND}/api/tematicas/get-festivities`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const result = await response.json();
      setFestividades(result?.data || []);
    } catch (err) {
      console.log('e', err);
    }
  };

  useEffect(() => {
    setCountsReady(true);
    fetchFestividades();
  }, []);

  // Pulsa el ícono del carrito cada vez que cambia la cantidad total de
  // unidades (no solo productos distintos, así también pulsa al sumar
  // cantidad de algo que ya estaba en el carrito), pero no en el primer
  // render post-hidratación (ahí el cambio viene de localStorage, no de una acción del usuario).
  const [cartPulse, setCartPulse] = useState(false);
  const prevCartTotal = useRef<number | null>(null);

  useEffect(() => {
    if (!countsReady) return;
    const total = carrito?.reduce((acc: number, item: any) => acc + (item.cantidad || 0), 0) || 0;
    const prev = prevCartTotal.current;
    prevCartTotal.current = total;
    if (prev !== null && prev !== total) {
      setCartPulse(true);
      const id = setTimeout(() => setCartPulse(false), 400);
      return () => clearTimeout(id);
    }
  }, [carrito, countsReady]);

  return {
    productosFavoritos,
    carrito,
    countsReady,
    cartPulse,
    menuIsOpen,
    setMenuIsOpen,
    isSticky,
    sentinelRef,
    festividades,
    anchorFest,
    openFest,
    handleOpenFest,
    handleCloseFest,
  };
};

export default useHook;
